// crewbit-admin/src/components/money/PaymentHistoryCard.tsx
//
// Spec: §InvoiceDetail.6 (B-N10). Renders PaymentLedgerRow[]. Hides on empty
// (calm-empty discipline per §1.4). Footer surfaces net (paid − refunded).

import { palette, spacing, typography } from '../../styles/tokens';
import { formatMoney, addMoney, subtractMoney } from '../../lib/money';
import type { PaymentLedgerRow } from '../../hooks/usePaymentLedger';
import type { Invoice } from '../../types';

interface Props {
  ledgerRows: PaymentLedgerRow[];
  invoice: Invoice;
}

const TABULAR: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };

const METHOD_LABEL: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  etransfer: 'E-transfer',
  cheque: 'Cheque',
  manual: 'Manual',
  stripe: 'Stripe',
  other: 'Other',
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function PaymentHistoryCard({ ledgerRows, invoice }: Props) {
  if (ledgerRows.length === 0) return null;

  let paid = '0.00';
  let refunded = '0.00';
  for (const r of ledgerRows) {
    if (r.direction === 'in') paid = addMoney(paid, String(r.amount));
    else refunded = addMoney(refunded, String(r.amount));
  }
  const net = subtractMoney(paid, refunded);

  return (
    <div
      style={{
        background: palette.white,
        border: `1px solid ${palette.g150}`,
        borderRadius: 12,
        padding: spacing.md,
        marginBottom: spacing.md,
      }}
    >
      <div style={{ fontSize: typography.size.caption, fontWeight: typography.weight.semibold, color: palette.g700, marginBottom: spacing.sm }}>
        Payment history
      </div>

      <div>
        {ledgerRows.map((r) => {
          const isRefund = r.direction === 'out';
          const sign = isRefund ? '−' : '+';
          const tone = isRefund ? palette.amber : palette.green;
          const methodLabel = METHOD_LABEL[r.payment_method] ?? r.payment_method;
          return (
            <div
              key={r.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: spacing.md,
                padding: `${spacing.sm}px 0`,
                borderBottom: `1px solid ${palette.g100}`,
                fontSize: typography.size.caption,
              }}
            >
              <div>
                <div style={{ color: palette.g800, fontWeight: typography.weight.medium }}>
                  {isRefund ? 'Refund' : 'Payment'} · {methodLabel}
                </div>
                <div style={{ color: palette.g500, fontSize: typography.size.micro, marginTop: 2 }}>
                  {fmtDate(r.date)}
                  {r.attribution_role ? ` · ${r.attribution_role}` : ''}
                </div>
              </div>
              <div style={{ color: tone, fontWeight: typography.weight.semibold, ...TABULAR }}>
                {sign}{formatMoney(r.amount)}
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex', justifyContent: 'space-between',
          paddingTop: spacing.sm, marginTop: spacing.xs,
          fontSize: typography.size.caption, color: palette.g700,
          fontWeight: typography.weight.semibold,
        }}
      >
        <span>Net received</span>
        <span style={TABULAR}>{formatMoney(net)}</span>
      </div>
      {invoice.total != null && (
        <div
          style={{
            display: 'flex', justifyContent: 'space-between',
            fontSize: typography.size.micro, color: palette.g500, marginTop: 2,
          }}
        >
          <span>Invoice total</span>
          <span style={TABULAR}>{formatMoney(invoice.total)}</span>
        </div>
      )}
    </div>
  );
}
